import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ThemeSlider — Editorial-Slider für die drei Themen-Bilder (Reisen, Auto-Services, Business).
 *
 * Bug-Fix: statt drei einzelner FullBleedImage-Sections (die auf schmalen Viewports
 * mit `aspect-[21/9]` unschön geschnitten wirkten) → EIN Slider mit:
 *   · `aspect-[16/10]` mobile, `aspect-[21/9]` desktop
 *   · `object-cover` mit `object-position: center` — Gesichter/Fokus bleiben zentriert
 *   · Motion-Reduce-freundliche Slide-Übergänge (translateX statt Opacity-Flash)
 *   · Auto-Advance alle 6 s (Pausiert bei Hover / Focus / Reduced Motion)
 *   · Prev/Next-Buttons + Dot-Pagination
 *   · Titel & Kicker OVER dem Bild (konvertierungsstark, emotional)
 */
const SLIDES = [
  {
    src: '/nav/reisen.webp',
    kicker: 'Reisen',
    title: 'Hotel-Buchung, die Tanken bezahlt.',
    caption: '1,6 Mio Häuser weltweit. Jede Buchung verwandelt sich in FuelGood-Guthaben — direkt auf deine Card.',
    testId: 'slide-reisen',
  },
  {
    src: '/nav/auto-services.webp',
    kicker: 'Auto-Services',
    title: 'Alle Auto-Services. Ein Konto.',
    caption: 'TÜV, Öl, Reifen, Werkstatt & Pannenhilfe — abgedeckt durch deine Reisen. Nie wieder Papierkrieg.',
    testId: 'slide-auto-services',
  },
  {
    src: '/nav/business.webp',
    kicker: 'Business',
    title: 'Flotte. Ohne Reibung.',
    caption: 'Ein Portal, ein Ansprechpartner, ein Wallet. Für Ihr Team, Ihr Fuhrpark, Ihre Geschäftsreisen.',
    testId: 'slide-business',
  },
];

export default function ThemeSlider() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduced || paused) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(timerRef.current);
  }, [paused]);

  const go = (n) => setIdx((n + SLIDES.length) % SLIDES.length);
  const next = () => go(idx + 1);
  const prev = () => go(idx - 1);

  return (
    <section
      className="w-full py-20 md:py-28 bg-white"
      data-testid="landing-theme-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Kicker über dem Slider */}
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-emerald-700 font-medium mb-2">Ein Ökosystem · Drei Erlebnisse</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-stone-900 tracking-[-0.03em] leading-[1.02] max-w-2xl">
              Alles verbunden. Nichts vergessen.
            </h2>
          </div>
          {/* Dots + Arrows */}
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              aria-label="Vorheriges Bild"
              className="w-9 h-9 rounded-full border border-stone-300 flex items-center justify-center text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
              data-testid="slider-prev"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              onClick={next}
              aria-label="Nächstes Bild"
              className="w-9 h-9 rounded-full border border-stone-300 flex items-center justify-center text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
              data-testid="slider-next"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Slide-Bühne */}
        <div className="relative rounded-3xl overflow-hidden bg-stone-100 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.35)]">
          <div
            ref={trackRef}
            className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateX(-${idx * 100}%)` }}
          >
            {SLIDES.map((s, i) => (
              <div
                key={s.src}
                className="w-full flex-shrink-0 bg-stone-900"
                data-testid={s.testId}
                aria-hidden={i !== idx}
              >
                {/* Bild-Bühne: mobile Stack (16:9 Bild + Copy separat darunter) · sm+ overlay */}
                <div className="relative aspect-[16/9]">
                  <img
                    src={s.src}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: 'center' }}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    fetchPriority={i === 0 ? 'high' : 'auto'}
                  />
                  {/* Gradient + Overlay-Copy nur ab sm — Mobile hat Copy unter dem Bild, damit das Model komplett sichtbar bleibt */}
                  <div className="hidden sm:block absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />
                  <div className="hidden sm:block absolute inset-x-0 bottom-0 max-w-2xl p-6 sm:p-10 lg:p-14">
                    <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-emerald-400 font-medium mb-3">
                      {s.kicker}
                    </p>
                    <h3 className="font-display text-white text-2xl sm:text-3xl lg:text-4xl tracking-[-0.03em] leading-[1.05] mb-3">
                      {s.title}
                    </h3>
                    <p className="text-white/80 text-xs sm:text-sm leading-relaxed max-w-md">
                      {s.caption}
                    </p>
                  </div>
                </div>
                {/* Mobile Copy — unterhalb des Bildes, auf dunklem Grund. Model bleibt oben komplett sichtbar. */}
                <div className="sm:hidden p-5">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 font-medium mb-2">
                    {s.kicker}
                  </p>
                  <h3 className="font-display text-white text-xl tracking-[-0.03em] leading-[1.1] mb-2">
                    {s.title}
                  </h3>
                  <p className="text-white/70 text-xs leading-relaxed">
                    {s.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Dot-Pagination — mittig zentriert, kollidiert weder mit Copy (links) noch mit Cookie-Banner (rechts unten) */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 sm:bottom-4 flex items-center bg-black/30 backdrop-blur-md rounded-full px-2 py-0.5">
            {SLIDES.map((s, i) => (
              <button
                key={s.src}
                onClick={() => go(i)}
                aria-label={`Zu Slide ${i + 1}`}
                data-testid={`slider-dot-${i}`}
                className="p-[9px] flex items-center justify-center"
              >
                <span className={`block h-1.5 rounded-full transition-all ${
                  i === idx ? 'w-8 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'
                }`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
